import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Email Attachment Schema
 */
export const EmailAttachmentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  sizeBytes: z.number(),
  mimeType: z.string().optional(),
  s3Url: z.string(),
});

/**
 * Document Association Schema
 */
export const DocumentAssociationSchema = z.object({
  id: z.string(),
  document: z.object({
    id: z.string(),
    name: z.string(),
    originalFileName: z.string(),
    fileSizeBytes: z.number().nullable(),
    mimeType: z.string().nullable(),
    url: z.string(),
    contentId: z.string().nullable(),
  }),
});

/**
 * Email Classification Schema
 */
export const EmailClassificationSchema = z.object({
  id: z.string(),
  classificationResult: z.string(),
  confidenceScore: z.number(),
  reasoning: z.string().nullable(),
  createdAt: z.string(),
});

/**
 * Translated Email Content Schema
 */
export const TranslatedEmailContentSchema = z.object({
  subject: z.string().optional(),
  body: z.string().optional(),
  isHtml: z.boolean().optional(),
});

/**
 * Email Thread Email Schema
 */
export const EmailThreadEmailSchema = z.object({
  id: z.string(),
  messageId: z.string(),
  conversationId: z.string().nullable(),
  from: z.string().nullable(),
  to: z.string(),
  cc: z.string().nullable(),
  subject: z.string().nullable(),
  textContent: z.string().nullable(),
  htmlContent: z.string().nullable(),
  messageBodyHtml: z.string().nullable().optional(),
  attachmentCount: z.number().nullable(),
  service: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  detectedLanguage: z.string().nullable().optional(),
  translatedTextContent: z
    .record(z.string(), z.union([z.string(), TranslatedEmailContentSchema]))
    .nullable()
    .optional(),
  classification: EmailClassificationSchema.nullable(),
  attachments: z.array(EmailAttachmentSchema).optional(),
  documentAssociations: z.array(DocumentAssociationSchema).optional(),
});

/**
 * Email Thread Response Schema
 */
export const EmailThreadResponseSchema = z.object({
  conversationId: z.string(),
  emails: z.array(EmailThreadEmailSchema),
  totalCount: z.number(),
  participantEmails: z.array(z.string()),
  earliestDate: z.string(),
  latestDate: z.string(),
});

/**
 * Get Email Thread Input Schema
 */
export const GetEmailThreadInputSchema = z.object({
  conversationId: z.string().min(1),
});

/**
 * Email Thread Contracts
 */
export const emailThreadContracts = {
  // Get an email thread by conversation ID
  getThread: oc
    .input(GetEmailThreadInputSchema)
    .output(EmailThreadResponseSchema)
    .route({
      method: "GET",
    }),
} as const;

// Export types
export type EmailAttachment = z.infer<typeof EmailAttachmentSchema>;
export type DocumentAssociation = z.infer<typeof DocumentAssociationSchema>;
export type EmailClassification = z.infer<typeof EmailClassificationSchema>;
export type TranslatedEmailContent = z.infer<
  typeof TranslatedEmailContentSchema
>;
export type EmailThreadEmail = z.infer<typeof EmailThreadEmailSchema>;
export type EmailThreadResponse = z.infer<typeof EmailThreadResponseSchema>;
export type GetEmailThreadInput = z.infer<typeof GetEmailThreadInputSchema>;
